import { useLocation } from "wouter";
import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { getNotifications, getNotificationCounts, markNotificationAsRead } from "@/lib/api/notifications";
import { formatDistanceToNow } from "date-fns";
import { Bell, MessageSquare, UserPlus, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";

type HeaderProps = {
  onOpenMobileMenu: () => void;
};

export default function Header({ onOpenMobileMenu }: HeaderProps) {
  const [location] = useLocation();
  const { user } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Fetch notification counts
  const { data: notificationCounts } = useQuery({
    queryKey: ["notification-counts"],
    queryFn: getNotificationCounts,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch recent notifications when dropdown is open
  const { data: notifications } = useQuery({
    queryKey: ["notifications", "recent"],
    queryFn: () => getNotifications({ unread_only: false, limit: 10 }),
    enabled: showNotifications,
  });

  // Mark notification as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: markNotificationAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notification-counts"] });
    },
  });

  if (!user) return null;
  
  // Get current page title based on location
  const getPageTitle = () => {
    switch (location) {
      case "/dashboard":
        return "Dashboard";
      case "/players":
        return "Player Database";
      case "/shortlists":
        return "Shortlists";
      case "/email-analytics":
        return "Email Analytics";
      case "/customization":
        return "Customization";
      case "/feedback":
        return "Feedback & Support";
      case "/profile":
        return "Profile Settings";
      default:
        return "Dashboard";
    }
  };
  
  return (
    <header className="bg-gradient-to-r from-white to-cyan-50 shadow-sm border-b border-cyan-100">
      <div className="flex justify-between items-center px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex items-center md:hidden">
          <button 
            type="button" 
            className="text-gray-600 hover:text-cyan-600 transition-colors duration-200"
            onClick={onOpenMobileMenu}
          >
            <i className="fas fa-bars text-xl"></i>
          </button>
          <h1 className="ml-3 text-xl font-bold bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">
            Bloom
          </h1>
        </div>
        <div className="hidden md:block">
          <h2 className="text-lg font-medium bg-gradient-to-r from-cyan-700 to-teal-700 bg-clip-text text-transparent">
            {getPageTitle()}
          </h2>
        </div>
        <div className="flex items-center space-x-4">
          
        </div>
      </div>
    </header>
  );
}
